import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Lightbulb, Network, Sparkles, ArrowUpRight } from 'lucide-react';
import Reveal from '../Reveal';

export default function ClubHomeSection() {
  return (
    <section
      className="relative py-20 md:py-28 overflow-hidden"
      data-testid="home-club-section"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-bg opacity-15" />
        <div
          className="absolute left-1/2 top-0 -translate-x-1/2 w-[1200px] h-[600px] rounded-full blur-[140px]"
          style={{ background: 'radial-gradient(ellipse, rgba(198,242,78,0.12), rgba(103,232,249,0.08) 50%, transparent 70%)' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="rounded-[32px] border-2 border-lime/30 p-8 md:p-14 relative overflow-hidden" style={{ background: 'linear-gradient(145deg, rgba(10,18,48,0.9) 0%, rgba(14,24,56,0.6) 60%, rgba(198,242,78,0.08) 100%)' }}>
            <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />

            {/* Members only pill */}
            <div className="relative flex items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lime/15 border border-lime/40 text-lime text-[11px] font-mono-t uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse" /> members only
              </span>
              <span className="text-white/40 text-xs font-mono-t">· accesso riservato</span>
            </div>

            <div className="relative grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
              <div>
                <p className="mono-label mb-3">_digital tech solutions club</p>
                <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl text-white leading-[1.02] mb-5">
                  Non cercare solo opportunita.<br />
                  <span className="gradient-text">Entra nel luogo in cui possono nascere.</span>
                </h2>
                <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mb-6">
                  Imprenditori, professionisti e persone con nuove idee si incontrano per condividere competenze, creare collaborazioni e sviluppare nuovi progetti.
                </p>

                {/* IDEA → NETWORK → PROGETTO → OPPORTUNITÀ animated flow */}
                <div className="mb-8 py-5 px-5 rounded-2xl border border-white/10 bg-white/[0.02]">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    {[
                      { icon: Lightbulb, label: 'IDEA',       color: '#fcd34d' },
                      { icon: Users,     label: 'NETWORK',    color: '#67e8f9' },
                      { icon: Sparkles,  label: 'PROGETTO',   color: '#f0abfc' },
                      { icon: Network,   label: 'OPPORTUNITA', color: '#c6f24e' },
                    ].map((step, i, arr) => (
                      <React.Fragment key={step.label}>
                        <div className="flex items-center gap-2.5 group">
                          <span
                            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all group-hover:scale-110"
                            style={{ background: `${step.color}20`, border: `1px solid ${step.color}60` }}
                          >
                            <step.icon size={16} style={{ color: step.color }} />
                          </span>
                          <span className="font-display font-bold text-xs md:text-sm text-white tracking-wider">{step.label}</span>
                        </div>
                        {i < arr.length - 1 && (
                          <span className="hidden md:block text-white/30 font-mono-t text-xs">
                            <ArrowUpRight size={14} className="rotate-45 inline" />
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/club"
                    data-testid="home-club-cta-discover"
                    className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-lime text-background font-semibold btn-glow transition-all hover:scale-105"
                  >
                    <Sparkles size={16} /> Scopri il Club <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
                  </Link>
                  <Link
                    to="/club#accesso"
                    data-testid="home-club-cta-join"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-lime/60 text-white font-semibold hover:bg-lime hover:text-background transition-all hover:scale-105"
                  >
                    Entra nel network <ArrowUpRight size={18} />
                  </Link>
                </div>
                <p className="text-white/40 text-xs font-mono-t mt-4">Accesso riservato ai membri.</p>
              </div>

              {/* Visual: network nodes */}
              <div className="relative hidden lg:block">
                <div className="relative aspect-square max-w-sm mx-auto">
                  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
                    <defs>
                      <radialGradient id="clubGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#c6f24e" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="#c6f24e" stopOpacity="0" />
                      </radialGradient>
                    </defs>
                    <circle cx="100" cy="100" r="80" fill="url(#clubGlow)" />
                    {/* Rays */}
                    {[0, 60, 120, 180, 240, 300].map((a, i) => {
                      const r = 70;
                      const rad = (a * Math.PI) / 180;
                      const x = 100 + Math.cos(rad) * r;
                      const y = 100 + Math.sin(rad) * r;
                      return (
                        <g key={i}>
                          <line x1="100" y1="100" x2={x} y2={y} stroke="rgba(198,242,78,0.3)" strokeWidth="0.5" strokeDasharray="2 2" />
                          <circle cx={x} cy={y} r="7" fill="#0a1230" stroke={i % 2 ? '#67e8f9' : '#c6f24e'} strokeWidth="1" />
                          <circle r="1.5" fill={i % 2 ? '#67e8f9' : '#c6f24e'} opacity="0.9">
                            <animateMotion dur={`${3 + i * 0.3}s`} repeatCount="indefinite" path={`M 100 100 L ${x} ${y}`} />
                          </circle>
                        </g>
                      );
                    })}
                    {/* Center brain-like node */}
                    <circle cx="100" cy="100" r="14" fill="#0a1230" stroke="#c6f24e" strokeWidth="1.5">
                      <animate attributeName="r" from="12" to="16" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <text x="100" y="104" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#c6f24e" fontFamily="monospace">DTS</text>
                  </svg>
                  <div className="absolute top-3 right-3 rounded-full px-3 py-1 glass text-[10px] font-mono-t text-lime border border-lime/20">
                    _community privata
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
